import gsap from 'gsap';
import { assetUrl } from './asset-url.js';
const photos = [
  'assets/img/img_01.jpg',
  'assets/img/img_02.jpg',
  'assets/img/img_03.jpg',
  'assets/img/img_04.jpg',
  'assets/img/img_05_new.jpg',
  'assets/img/img_06_new.jpg',
  'assets/img/img_07.jpg',
  'assets/img/img_08.jpg',
].map(assetUrl);

const body = document.body;
const bg = document.getElementById('animated-bg');
const container = document.getElementById('photos-bg-container');
const studio = gsap.utils.toArray('.studio');
const fade = { duration: 0.1, ease: 'power2.out' };
const instagramUrl = 'https://www.instagram.com/six_roubo/';
const SVG_NS = 'http://www.w3.org/2000/svg';
const LETTER_HIT_PAD = 12;

function setupLetterHitAreas() {
  const paths = gsap.utils.toArray('.letter');

  return paths.map((path) => {
    const group = document.createElementNS(SVG_NS, 'g');
    group.classList.add('letter-group');
    path.parentNode.insertBefore(group, path);
    group.appendChild(path);
    path.classList.remove('letter');
    path.classList.add('letter-shape');

    const { x, y, width, height } = path.getBBox();
    const hit = document.createElementNS(SVG_NS, 'rect');
    hit.classList.add('letter-hit');
    hit.setAttribute('x', x - LETTER_HIT_PAD);
    hit.setAttribute('y', y - LETTER_HIT_PAD);
    hit.setAttribute('width', width + LETTER_HIT_PAD * 2);
    hit.setAttribute('height', height + LETTER_HIT_PAD * 2);
    group.insertBefore(hit, path);

    return { path, hit };
  });
}

const letters = setupLetterHitAreas();
const letterPaths = letters.map(({ path }) => path);

const bg1 = 'linear-gradient(0deg, #ff0004, #ff0000, #ff6d53, #ff0004, #ff0004)';
const bg2 = 'linear-gradient(0deg, #ff0004, #b91319, #ff0000, #ff6d53, #ff0004)';
const isMobile = window.matchMedia('(max-width: 767px), (pointer: coarse)').matches;

// iOS Safari can still scroll via touchmove even with overflow hidden.
if (isMobile) {
  document.addEventListener(
    'touchmove',
    (e) => {
      const target = e.target;
      if (target && target.closest && target.closest('a, button, input, textarea, select, label')) return;
      e.preventDefault();
    },
    { passive: false },
  );
}

/** Load one image and return the <img> once it's ready (or failed). */
function loadImage(src) {
  return new Promise((resolve) => {
    const img = document.createElement('img');
    img.className = 'photo-bg';
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve(img);
    };
    img.onload = done;
    img.onerror = done;
    img.src = src;
  });
}

let active = null;
let selectedViaLetter = false;
let images = [];
let bgTween = null;

function activateLetter(i) {
  if (!images[i]) return;
  active = i;
  gsap.to(bg, { opacity: 0, ...fade });
  gsap.to(images, { opacity: 0, ...fade });
  gsap.to(images[i], { opacity: 1, ...fade });
  gsap.to(letterPaths, { opacity: 1, fill: '#000', ...fade });
  gsap.to(letterPaths[i], { opacity: 1, fill: '#FFF', ...fade });
  gsap.to(studio, { opacity: 1, fill: '#FFF', ...fade });
  gsap.to('body', { color: '#FFF', ...fade });
}

function deactivateActiveLetter() {
  if (active == null) return;
  active = null;
  selectedViaLetter = false;
  gsap.to(bg, { opacity: 1, ...fade });
  gsap.to(images, { opacity: 0, ...fade });
  gsap.to(letterPaths, { opacity: 1, fill: '#000', ...fade });
  gsap.to(studio, { opacity: 1, fill: '#000', ...fade });
  gsap.to('body', { color: '#000', ...fade });
}

function setupInteractions() {
  if (isMobile) {
    letters.forEach(({ hit }, i) => {
      hit.addEventListener(
        'pointerdown',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (active === i) {
            deactivateActiveLetter();
            return;
          }
          selectedViaLetter = true;
          activateLetter(i);
        },
        { passive: false },
      );
    });

    body.addEventListener(
      'pointerdown',
      (e) => {
        if (e.target && e.target.closest && e.target.closest('a, button, input, textarea, select, label, .letter-hit, .letter-group')) {
          return;
        }

        if (active == null) {
          selectedViaLetter = false;
          activateLetter(0);
        } else if (selectedViaLetter) {
          deactivateActiveLetter();
        } else if (active < letters.length - 1) {
          activateLetter(active + 1);
        } else {
          deactivateActiveLetter();
        }
      },
      { passive: true },
    );
  } else {
    letters.forEach(({ hit }, i) => {
      hit.addEventListener('mouseenter', () => activateLetter(i));
      hit.addEventListener('mouseleave', () => {
        if (active !== i) return;
        deactivateActiveLetter();
      });
      hit.addEventListener('click', () => {
        window.open(instagramUrl, '_blank', 'noopener,noreferrer');
      });
    });
  }
}

async function init() {
  // 1) Wait until every photo is fully loaded
  images = await Promise.all(photos.map(loadImage));
  images.forEach((img) => container.append(img));
  gsap.set(images, { opacity: 0 });

  // 2) Start bg animation only after content is ready
  bgTween = gsap.fromTo(
    'body',
    { background: bg1 },
    { background: bg2, ease: 'none', duration: 10, yoyo: true, repeat: -1 },
  );

  // 3) Reveal the page, then enable interactions
  body.classList.add('is-ready');
  gsap.fromTo(
    body,
    { opacity: 0 },
    {
      opacity: 1,
      duration: 0.4,
      ease: 'power2.out',
      onComplete: setupInteractions,
    },
  );
}

init();
